import { useState, FormEvent, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';
import { Trans, useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { useAuth } from '@breath-lab/state';
import { ROUTES } from '@app/routes';
import Modal from '../Modal/Modal';
import Button from '../Button/Button';
import IconButton from '../IconButton/IconButton';
import styles from './AuthModal.module.css';

function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" />
      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
    </svg>
  );
}

function CloseIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <line x1="1" y1="1" x2="13" y2="13" />
      <line x1="13" y1="1" x2="1" y2="13" />
    </svg>
  );
}

function ArcSpinner() {
  // Echoes the player's ArcRing — background ring + orbiting arc — sized for buttons.
  return (
    <span className={styles.arcSpinner} aria-hidden="true">
      <svg viewBox="0 0 32 32" width="18" height="18">
        <circle cx="16" cy="16" r="13" fill="none" stroke="currentColor" strokeOpacity="0.25" strokeWidth="1.5" />
        <circle cx="16" cy="16" r="13" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeDasharray="6 75" />
      </svg>
    </span>
  );
}

type LoadingMethod = 'google' | 'email' | null;

export default function AuthModal() {
  const { t } = useTranslation('auth');
  const { isLoginModalOpen, closeLoginModal, signInWithGoogle, signInWithEmail } = useAuth();
  const navigate = useNavigate();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loadingMethod, setLoadingMethod] = useState<LoadingMethod>(null);
  const loading = loadingMethod !== null;

  // Close on Escape key
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') closeLoginModal();
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [closeLoginModal]);

  // Prevent body scroll when open
  useEffect(() => {
    document.body.style.overflow = isLoginModalOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [isLoginModalOpen]);

  function reset() {
    setEmail('');
    setPassword('');
    setError('');
    setLoadingMethod(null);
  }

  function handleClose() {
    reset();
    closeLoginModal();
  }

  function navigateAfterSignIn() {
    const { user } = useAuth.getState();
    if (!user?.onboardedAt) {
      navigate(ROUTES.welcome, { replace: true });
      return;
    }
    const redirectTo = sessionStorage.getItem('authRedirectFrom') ?? ROUTES.loggedInHome;
    sessionStorage.removeItem('authRedirectFrom');
    navigate(redirectTo, { replace: true });
  }

  async function handleEmailSignIn(e: FormEvent) {
    e.preventDefault();
    if (loading) return;
    setError('');
    setLoadingMethod('email');
    try {
      await signInWithEmail(email, password);
      reset();
      navigateAfterSignIn();
    } catch (err) {
      setError(friendlyError(err, t));
      setLoadingMethod(null);
    }
  }

  async function handleGoogleSignIn() {
    if (loading) return;
    setError('');
    setLoadingMethod('google');
    try {
      await signInWithGoogle();
      navigateAfterSignIn();
    } catch (err) {
      setError(friendlyError(err, t));
    } finally {
      setLoadingMethod(null);
    }
  }

  return (
    <AnimatePresence>
      {isLoginModalOpen && (
        <Modal title={t('signIn')} onClose={handleClose}>
          <IconButton
            variant="ghost"
            label={t('close')}
            onClick={handleClose}
            className={styles.closeBtn}
          >
            <CloseIcon />
          </IconButton>

          <Button
            variant="secondary"
            size="md"
            onClick={handleGoogleSignIn}
            disabled={loading}
            aria-busy={loadingMethod === 'google'}
            className={styles.fullWidth}
          >
            {loadingMethod === 'google' ? <ArcSpinner /> : <GoogleIcon />}
            {loadingMethod === 'google' ? t('signingIn') : t('signInWithGoogle')}
          </Button>

          <p className={styles.oauthNote}>
            <Trans
              i18nKey="googleOAuthNote"
              ns="auth"
              components={{
                tosLink: <a href="/terms-of-service" target="_blank" rel="noopener noreferrer" />,
                privacyLink: <a href="/privacy-policy" target="_blank" rel="noopener noreferrer" />,
              }}
            />
          </p>

          <div className={styles.divider}>{t('dividerOr')}</div>

          {error && <p className={styles.error} role="alert">{error}</p>}

          <form className={styles.form} onSubmit={handleEmailSignIn}>
            <label htmlFor="auth-email" className={styles.srOnly}>{t('email')}</label>
            <input
              id="auth-email"
              className={styles.input}
              type="email"
              placeholder={t('email')}
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={loading}
              required
            />
            <label htmlFor="auth-password" className={styles.srOnly}>{t('password')}</label>
            <input
              id="auth-password"
              className={styles.input}
              type="password"
              placeholder={t('password')}
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={loading}
              required
            />
            <Link
              to={ROUTES.forgotPassword}
              className={`${styles.forgotLink} ${loading ? styles.linkDisabled : ''}`}
              onClick={(e) => { if (loading) e.preventDefault(); else handleClose(); }}
              tabIndex={loading ? -1 : 0}
              aria-disabled={loading}
            >
              {t('forgotPassword')}
            </Link>
            <Button
              variant="primary"
              size="md"
              type="submit"
              disabled={loading}
              aria-busy={loadingMethod === 'email'}
              className={styles.fullWidth}
            >
              {loadingMethod === 'email' ? (
                <>
                  <ArcSpinner />
                  {t('signingIn')}
                </>
              ) : (
                t('signIn')
              )}
            </Button>
          </form>

          <p className={`${styles.footer} ${loading ? styles.linkDisabled : ''}`}>
            {t('noAccount')}{' '}
            <Link
              to={ROUTES.signup}
              onClick={(e) => { if (loading) e.preventDefault(); else handleClose(); }}
              tabIndex={loading ? -1 : 0}
              aria-disabled={loading}
            >
              {t('noAccountSignUp')}
            </Link>
          </p>
        </Modal>
      )}
    </AnimatePresence>
  );
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function friendlyError(err: any, t: TFunction<'auth'>): string {
  const code: string = err?.code ?? '';
  switch (code) {
    case 'auth/invalid-credential':
    case 'auth/wrong-password':
    case 'auth/user-not-found':
      return t('errors.invalidCredentials');
    case 'auth/too-many-requests':
      return t('errors.tooManyAttempts');
    case 'auth/popup-closed-by-user':
      return '';
    default:
      return err?.message ?? t('errors.generic');
  }
}
